<script setup>
import { Link, useForm } from '@inertiajs/vue3';

import CheckboxInput from '@/Components/CheckboxInput.vue';
import PasswordInput from '@/Components/PasswordInput.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import useConfig from '@/Composables/useConfig';
import AuthLayout from '@/Layouts/AuthLayout.vue';

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const config = useConfig();

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <AuthLayout :title="$t('auth.login')">
        <form class="space-y-6" @submit.prevent="submit">
            <TextInput
                id="email"
                v-model="form.email"
                type="email"
                name="email"
                :label="$t('auth.email')"
                :placeholder="$t('auth.email')"
                :error="form.errors.email"
                required
                autofocus
                autocomplete="username"
                tabindex="1"
            />

            <PasswordInput
                id="password"
                v-model="form.password"
                name="password"
                :label="$t('auth.password')"
                :placeholder="$t('auth.password')"
                :error="form.errors.password"
                required
                autocomplete="current-password"
                tabindex="2"
            >
                <template v-if="config.fortify.canResetPasswords" #extra>
                    <Link
                        :href="route('password.request')"
                        class="link"
                        tabindex="5"
                    >
                        {{ $t('auth.forgot_password') }}
                    </Link>
                </template>
            </PasswordInput>

            <CheckboxInput
                id="remember"
                v-model:checked="form.remember"
                name="remember"
                :error="form.errors.remember"
                tabindex="3"
            >
                <template #label>{{ $t('auth.remember_me') }}</template>
            </CheckboxInput>

            <PrimaryButton
                type="submit"
                class="w-full"
                :disabled="form.processing"
                tabindex="4"
            >
                {{ $t('auth.login') }}
            </PrimaryButton>
        </form>

        <template v-if="config.fortify.canRegister" #extra>
            <p>
                {{ $t('auth.not_a_member') }}
                {{ ' ' }}
                <Link :href="route('register')" class="link">
                    {{ $t('auth.register') }}
                </Link>
            </p>
        </template>
    </AuthLayout>
</template>
